/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import * as Yup from "yup";

// css
import "../students/Students.css";
import "./Settings.css";

// api
import schoolApi from "../../api/School";

// components
import MainHeader from "../../components/headers/mainHeader/MainHeader";
import FullPageLoader from "../../components/loader/FullPageLoader";
import Card from "../../components/card/Card";
import { Form, Formik } from "formik";
import ButtonIcon from "../../components/buttons/buttonIcon/ButtonIcon";
import { AuthContext } from "../Root/ProtectedRoute";
import { 
  empty,
  errorResponse,
  isObject,
  isString,
  prepareResponseData
} from "../../Utilities/utils";
import SettingsWrapper from "../../components/appWrapper/SettingsWrapper";
import SelectField from "../../components/form/SelectField";
import InputFieldChange from "../../components/form/InputFieldChange";
import FlagContainer from "../../components/error/FlagContainer";
import colors from "../../config/colors";

const required = "This field is required.";
const validateSchemaTerms = Yup.object().shape({
  no_of_cas: Yup.number().required("Number of Assessment is required"),
});

const AssessmentSettings = ({ ...props }) => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [initialValuesCAs, setInitialValuesCAs] = useState({ no_of_cas: 2 });
  const [isLoading, setIsLoading] = useState(false);
  const [assessmentMaxScores, setAssessmentMaxScores] = useState({});
  const [caSum, setCaSum] = useState(0);
  const toastTR = useRef(null);
  const validationUpdateAssessmentSchema = useMemo(
    () =>
      Yup.object().shape({
        ...Object.fromEntries(
          Array.from({ length: initialValuesCAs?.no_of_cas || 2 }, (_, index) => [
            `ca${index + 1}_max`,
            Yup.string().required(required),
          ])
        ),

        exam_max: Yup.string().required(required),
      }),
    [initialValuesCAs?.no_of_cas]
  );

  useEffect(() => {
    const _assessmentMaxScores =
      !empty(user.assessmentMaxScores) && isObject(user.assessmentMaxScores)
        ? user.assessmentMaxScores
        : {};
    setAssessmentMaxScores(_assessmentMaxScores);
    const caTotal = Object.entries(_assessmentMaxScores)
      .filter(([key]) => {
        if (key.startsWith("exam")) {
          return true;
        }

        if (key.startsWith("ca")) {
          const caNumber = Number(key.match(/^ca(\d+)/)?.[1]);

          return caNumber <= (user?.noOfCAs || 2);
        }

        return false;
      })
      .reduce((sum, [, value]) => {
        return sum + Number(value || 0);
      }, 0);
    setCaSum(caTotal);
    setInitialValuesCAs({
      no_of_cas: user?.noOfCAs || 2,
    });
  }, []);

  // alert functions
  const responseDialog = (severity = null, summary = null, detail = null) => {
    toastTR?.current?.show({
      severity,
      summary,
      detail,
      life: 8000,
    });
  };

  const handleAssessmentSettingsSubmit = async (values) => {
    if (!isLoading) setIsLoading(true);
    try {
      if (empty(values)) {
        return responseDialog(
          "error",
          "Error Alert",
          "Something went wrong! Try again later."
        );
      }

      const filteredValues = Object.fromEntries(
        Object.entries(values).filter(([key]) => {
          const match = key.match(/^ca(\d+)_max$/);

          if (!match) return true; // Keep exam and other non-CA fields

          return Number(match[1]) <= (user?.noOfCAs || 2);
        })
      );
      const total = Object.values(filteredValues).reduce((sum, val) => sum + Number(val || 0), 0);
      if (total !== 100) {
        return responseDialog( 
          "error",
          "Error Alert",
          "Assessment cumulative sum should be equal to 100"
        );
      }

      const response = await schoolApi.updateAssessmentMaxScores(
        filteredValues
      );

      const response_data = prepareResponseData(response);
      if (empty(response_data.success)) {
        if (
          !empty(response_data) &&
          !empty(response_data.statusCodeType) &&
          response_data.statusCodeType === "unauthorized"
        ) {
          return navigate("/");
        }
        return responseDialog(
          "error",
          "Error Alert",
          !empty(response_data) && !empty(response_data.response)
            ? response_data.response
            : "Failed to updated assessment maximum scores!"
        );
      }

      return responseDialog(
        "success",
        "Successful",
        "Maximum assessment scores updated successfully."
      );
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setIsLoading(false);
    }
  };


    /**
     * handle assessment count
     * @param {*} values
     * @returns
     */
    const handleCACount = async (values) => {
      try {
        if (!isLoading) setIsLoading(true);
        if (empty(values)) {
          responseDialog("error", "Error Alert", "Something went wrong.");
        }
  
        const response = await schoolApi.updateNoOfCAs(values);
        const response_data = prepareResponseData(response);
        if (empty(response_data) || !response_data.success) {
          return responseDialog(
            "error",
            "Error Alert",
            !empty(response_data) && !empty(response_data.response)
              ? response_data.response
              : "Failed to update number of CAs!"
          );
        } 
        responseDialog(
          "success",
          "Success",
          `Number of CAs updated successfully.`
        );
        return setTimeout(() => {
          window.location.reload();
        })
      } catch (error) {
        responseDialog("error", "Error Alert!", errorResponse(error));
      } finally {
        setIsLoading(false);
      }
    };

    const handleCASum = (setFieldValue, name) => async (e) => {
      const value = isString(e?.target?.value)
        ? e.target.value
        : "";

      setFieldValue(name, value);

        const caTotal = Object.entries(assessmentMaxScores)
          .filter(([key]) => {
            if (key.startsWith("exam")) {
              return true;
            }

            if (key.startsWith("ca")) {
              const caNumber = Number(key.match(/^ca(\d+)/)?.[1]);

              return caNumber <= (user?.noOfCAs || 2);
            }

            return false;
        })
        .reduce((sum, [key, currentValue]) => sum + Number(key === name ? value || 0 : currentValue || 0), 0);

        if (caTotal > 100) {
          return responseDialog("error", "Error Alert!", "Total assessment score should be equal to 100");
        }

      setCaSum(caTotal);
    };

  return (
    <>
      <SettingsWrapper {...props}>
        <main>
          <div className="container flex-center-top">
            <MainHeader title="Settings" />
            <div className="flex flex-direction-row col-g-12 w-100pc">
              <Card
                children={
                  <Formik
                    enableReinitialize
                    initialValues={initialValuesCAs}
                    validationSchema={validateSchemaTerms}
                    onSubmit={handleCACount}
                  >
                    {({ values, handleChange }) => (
                      <Form style={{ width: "100%" }}>
                        <div
                          style={{ marginTop: 10, fontSize: 18, color: colors.primary }}
                        >
                          Number of CAs
                        </div>
                        <div className="app_input_group  md-w-50pc">
                          <SelectField
                            labelTitle="Select Number of CAs"
                            placeholder="Enter number of CAs"
                            name="no_of_cas"
                            required={true}
                            options={[
                              { id: 0, title: 1 },
                              { id: 1, title: 2 },
                              { id: 2, title: 3 },
                            ]}
                            height={50}
                            valueKey="title"
                            display="title"
                            width={100}
                            selectedOption={values.no_of_cas}
                            handleChangeFunc={handleChange}
                          />
                        </div>
                        <div
                          style={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "flex-start",
                          }}
                        >
                          <ButtonIcon
                            height={45}
                            marginTop={2}
                            color="#ffffff"
                            backgroundColor="#633ccd"
                            width={100}
                            borderColor="#633ccd"
                            buttonText="Update"
                            type="submit"
                          />
                        </div>
                      </Form>
                    )}
                  </Formik>
                }
                addStyle="settings-body px-20 mb-50"
              />
            </div>
            <div className="flex flex-direction-row col-g-12 w-100pc">
              <Card
                children={
                  <div>
                    <MainHeader titleColor="#633ccd" title="Assessment Settings" />
                    { caSum !== 100 &&
                      <FlagContainer backgroundColor={colors.danger} color={colors.white} content="The assessment cumulative sum should be 100." />
                    }
                    <div>
                      <Formik
                        enableReinitialize
                        initialValues={{
                          ...Object.fromEntries(
                            Array.from({ length: initialValuesCAs?.no_of_cas || 2 }, (_, idx) => {
                              const key = `ca${idx + 1}_max`;

                              return [
                                key,
                                !empty(assessmentMaxScores?.[key])
                                  ? assessmentMaxScores[key]
                                  : "",
                              ];
                            })
                          ),

                          exam_max:
                            !empty(assessmentMaxScores?.exam_max)
                              ? assessmentMaxScores.exam_max
                              : "",
                        }}    
                        validationSchema={validationUpdateAssessmentSchema}
                        onSubmit={handleAssessmentSettingsSubmit}
                      >
                        {({ values, handleChange, setFieldValue }) => (    
                          <Form style={{ width: "100%" }}>
                            <div className="app_input_group">
                              {
                                Array.from({ length: initialValuesCAs?.no_of_cas || 2 }).map((_, index) => (
                                  <InputFieldChange
                                    placeholder="Enter ca1 maximum value"
                                    name={`ca${index + 1}_max`}
                                    height={50}
                                    required={true}
                                    type="number"
                                    labelTitle={`CA ${index + 1} Maximum Value`}
                                    key={index}
                                    value={values[`ca${index + 1}_max`]  ?? ''}
                                    onChange={handleCASum(setFieldValue, `ca${index + 1}_max`) }
                                  />
                                ))
                              }
                              <InputFieldChange
                                placeholder="Enter exam maximum value"
                                name="exam_max"
                                height={50}
                                type='number'
                                required={true}
                                labelTitle="Exam Maximum Value"
                                key={5}
                              />
                            </div>
                            <div className="mt-20"><strong>Assessment Max Sum:</strong><span style={{ color: caSum !== 100 ? colors.danger : colors.success }}> <strong>{caSum}</strong></span></div>
                            <div className="form-button-container">
                              <ButtonIcon
                                height={45}
                                marginTop={5}
                                color="#ffffff"
                                backgroundColor="#633ccd"
                                width={300}
                                borderColor="#633ccd"
                                buttonText="Assessment Settings"
                                type="submit"
                              />
                            </div>
                          </Form>
                        )}
                      </Formik>
                    </div>
                  </div>
                }
                addStyle="settings-body px-20"
              />
            </div>
          </div>
        </main>
        {isLoading && <FullPageLoader visible={isLoading} />}
        <Toast ref={toastTR} position="bottom-left" />
      </SettingsWrapper>
    </>
  );
};

export default AssessmentSettings;
